import { Component, inject } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { TranslateModule } from '@ngx-translate/core';
import { NavIconPairComponent } from '@shared/ui/nav-icon-pair/nav-icon-pair.component';

export interface MobileDockLauncherItem {
  id: number;
  title: string;
  iconName: string;
}

export interface MobileDockLauncherData {
  items: MobileDockLauncherItem[];
  titleKey: string;
  /** Módulo activo según URL / rail (highlight honesto). */
  activeId?: number | null;
}

@Component({
  selector: 'app-milinco-mobile-dock-launcher',
  standalone: true,
  imports: [TranslateModule, NavIconPairComponent],
  template: `
    <div class="dock-launcher" role="dialog" [attr.aria-label]="data.titleKey | translate">
      <!-- Handle vía chrome global del bottom sheet (milinco-sheet-handle). -->
      <h2 class="dock-launcher__title">{{ data.titleKey | translate }}</h2>
      <div class="dock-launcher__grid">
        @for (item of data.items; track item.id) {
          <button
            type="button"
            class="dock-launcher__item"
            [class.dock-launcher__item--active]="item.id === data.activeId"
            (click)="pick(item.id)"
          >
            <app-nav-icon-pair
              [icon]="item.iconName"
              [active]="item.id === data.activeId"
              iconClass="dock-launcher__icon"
            />
            <span class="dock-launcher__label">{{ item.title | translate }}</span>
          </button>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        background: var(--mat-sys-surface, #fff);
        color: var(--mat-sys-on-surface, #1a1c1e);
      }

      .dock-launcher {
        padding: 0.35rem 1rem calc(1rem + env(safe-area-inset-bottom, 0px));
        background: var(--mat-sys-surface, #fff);
      }

      .dock-launcher__title {
        margin: 0.35rem 0 0.85rem;
        font-size: 1rem;
        font-weight: 700;
        color: var(--mat-sys-on-surface);
      }

      .dock-launcher__grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(4.75rem, 1fr));
        gap: 0.65rem;
      }

      .dock-launcher__item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.35rem;
        min-height: 4.5rem;
        min-width: 48px;
        padding: 0.6rem 0.35rem;
        border: 1px solid color-mix(in srgb, var(--mat-sys-outline-variant) 70%, transparent);
        border-radius: 0.9rem;
        background: var(--mat-sys-surface-container-lowest, var(--mat-sys-surface, #fff));
        color: var(--mat-sys-on-surface);
        cursor: pointer;
        font: inherit;
        touch-action: manipulation;
      }

      .dock-launcher__item--active {
        border-color: transparent;
        background: color-mix(in srgb, var(--mat-sys-primary) 16%, var(--mat-sys-surface));
        color: var(--mat-sys-on-surface, #1a1c1e);
        box-shadow: inset 0 0 0 1.5px color-mix(in srgb, var(--mat-sys-primary) 55%, transparent);
      }

      .dock-launcher__item:hover,
      .dock-launcher__item:focus-visible {
        outline: none;
        border-color: color-mix(in srgb, var(--mat-sys-primary) 55%, transparent);
        background: color-mix(in srgb, var(--mat-sys-primary) 10%, var(--mat-sys-surface));
        color: var(--mat-sys-on-surface, #1a1c1e);
      }

      .dock-launcher__item--active:hover,
      .dock-launcher__item--active:focus-visible {
        background: color-mix(in srgb, var(--mat-sys-primary) 22%, var(--mat-sys-surface));
        color: var(--mat-sys-on-surface, #1a1c1e);
      }

      .dock-launcher__item--active app-nav-icon-pair,
      .dock-launcher__item--active .dock-launcher__icon {
        color: var(--text-accent, #5c7a00);
      }

      .dock-launcher__icon {
        /* Launcher de módulos: icono más presente que el tab bar (24px). */
        width: 1.75rem;
        height: 1.75rem;
        font-size: 1.75rem;
        color: inherit;
      }

      app-nav-icon-pair {
        flex: 0 0 auto;
        color: inherit;
      }

      .dock-launcher__label {
        font-size: 0.75rem;
        font-weight: 650;
        line-height: 1.2;
        text-align: center;
      }
    `,
  ],
})
export class MilincoMobileDockLauncherComponent {
  readonly data = inject<MobileDockLauncherData>(MAT_BOTTOM_SHEET_DATA);
  private readonly sheetRef = inject(MatBottomSheetRef<MilincoMobileDockLauncherComponent, number | null>);

  pick(id: number): void {
    this.sheetRef.dismiss(id);
  }
}
